// Full-bleed environment plate for a screen background.
//
// The plates (public-v2/plates/*, generated by tools/screen-dressing-batch.py) are deliberately
// rendered at NORMAL brightness rather than pre-darkened, so legibility is a runtime decision:
// the same plate sits under /menu and /play at different `scrim` values without a second
// generation. If a screen's UI grows denser, raise the scrim instead of regenerating art.
//
// Layering, back to front:
//   1. the plate itself, object-cover
//   2. a flat asphalt scrim at `scrim` opacity — the main legibility control
//   3. a bottom-weighted asphalt gradient, so footers and CTAs sitting low on the screen always
//      have something solid behind them even where the plate is bright down there
//
// Screens keep their own sodium/neon wash on top of this; the plate replaces flat asphalt as the
// floor of the stack, it does not replace the ambient lighting those washes provide.

interface ScreenPlateProps {
  /** Path under the public base, e.g. 'plates/parking_lot.webp'. */
  src: string;
  /**
   * Asphalt scrim opacity, 0-1. Rule of thumb: ~0.55 for a screen that is mostly a hero moment,
   * ~0.75 for a screen carrying a dense grid of tiles or cards over the top.
   */
  scrim?: number;
  /** CSS object-position for the plate — lets one 16:9 plate frame differently per screen. */
  position?: string;
}

export function ScreenPlate({ src, scrim = 0.7, position = 'center' }: ScreenPlateProps) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <img
        src={`${import.meta.env.BASE_URL}${src}`}
        alt=""
        className="h-full w-full object-cover"
        style={{ objectPosition: position }}
        // Backgrounds must never delay the first meaningful paint of the UI on top of them.
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-asphalt" style={{ opacity: scrim }} />
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, rgba(26,22,18,0.95) 0%, rgba(26,22,18,0.45) 35%, rgba(26,22,18,0) 70%)',
        }}
      />
    </div>
  );
}
